/**
 * 對話框組件 | Modal Dialog Component
 * 提供統一的對話框功能與無障礙支援
 * 注重使用者體驗：鍵盤操作、無障礙、動畫效果
 */

import { useEffect, useRef } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // 對話框開啟時的處理
  useEffect(() => {
    if (isOpen) {
      // 記錄當前焦點元素
      previousActiveElement.current = document.activeElement as HTMLElement;

      // 鎖定焦點在對話框內
      modalRef.current?.focus();

      // 鎖定背景滾動
      document.body.style.overflow = 'hidden';
    } else {
      // 恢復背景滾動
      document.body.style.overflow = '';

      // 恢復焦點到之前的元素
      previousActiveElement.current?.focus();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC 鍵關閉
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // 焦點陷阱（Focus Trap）
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);

    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // 響應式尺寸：移動端使用 max-w-full，桌面端使用指定尺寸
  const sizeClasses = {
    sm: 'max-w-full sm:max-w-sm',
    md: 'max-w-full sm:max-w-md',
    lg: 'max-w-full sm:max-w-lg',
    xl: 'max-w-full sm:max-w-xl',
  }[size];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 對話框內容 */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`relative bg-white border-2 border-[#e2e8f0] rounded-2xl shadow-2xl w-full ${sizeClasses} animate-modal-in max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 標題列 */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] bg-gradient-to-r from-slate-50 to-white">
            {title && (
              <h2
                id="modal-title"
                className="text-lg font-bold text-[#0f172a]"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-[#f1f5f9] flex items-center justify-center transition-colors"
                aria-label="關閉對話框"
              >
                <span className="text-[#64748b] hover:text-[#0f172a] text-xl">✕</span>
              </button>
            )}
          </div>
        )}

        {/* 內容區域 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>

        {/* 無障礙：螢幕閱讀器通知 */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {isOpen && '對話框已開啟'}
        </div>
      </div>
    </div>
  );
}

// 確認對話框組件
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = '確認',
  cancelLabel = '取消',
  type = 'warning',
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: 'warning' | 'danger' | 'info';
}) {
  const typeConfig = {
    warning: {
      icon: '⚠️',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-700',
      confirmClass: 'bg-amber-500 hover:bg-amber-600 text-white',
    },
    danger: {
      icon: '🗑️',
      bgColor: 'bg-gradient-to-br from-red-50 to-rose-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
      confirmClass: 'bg-red-500 hover:bg-red-600 text-white',
    },
    info: {
      icon: 'ℹ️',
      bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      confirmClass: 'bg-blue-500 hover:bg-blue-600 text-white',
    },
  }[type];

  const config = typeConfig;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="text-center space-y-4">
        {/* 圖示 */}
        <div className={`w-16 h-16 ${config.bgColor} ${config.borderColor} border-2 rounded-2xl flex items-center justify-center mx-auto`}>
          <span className="text-3xl">{config.icon}</span>
        </div>

        {/* 訊息 */}
        <p className="text-[#475569] leading-relaxed">{message}</p>

        {/* 按鈕 */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border-2 border-[#e2e8f0] text-[#0f172a] rounded-lg font-medium hover:bg-[#f8fafc] transition-all hover-lift"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all hover-lift ${config.confirmClass} shadow-md`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
